// TimeGrid — примитив временной сетки календаря (День/Неделя): ось времени слева
// (24 подписи HH:00) и колонки дней с линиями часов/получасов и событиями.
// События раскладываются per-day (toDayFragments + layoutDaySpans, §6.1 шаг 5);
// now-line рендерится в колонке сегодняшнего дня (S1).
import { Box, Button, Typography } from '@mui/material';
import { isToday, format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import type { CalendarEvent } from '@/api/calendar';
import {
  toDayFragments,
  layoutDaySpans,
  MINUTES_PER_DAY,
  HOUR_HEIGHT_PX,
  SLOT_MINUTES,
  MAX_VISIBLE_EVENTS_PER_COLUMN,
} from '@/lib/timeGridLayout';
import type { PositionedSpan } from '@/lib/timeGridLayout';
import { EventChip } from './EventChip';
import { NowLine } from './NowLine';

/** Ширина оси времени (px) — общая для TimeGrid и заголовков дней TimeGridWeek. */
export const TIME_AXIS_WIDTH_PX = 56;

interface TimeGridProps {
  days: Date[]; // локальные дни (колонки): 1 — «День», 7 — «Неделя»
  events: CalendarEvent[]; // события диапазона (allDay отфильтрованы в toDayFragments)
  onEventClick?: ((event: CalendarEvent) => void) | undefined; // клик по событию → detail
}

/** Колонка одного дня: линии часов/получасов, absolute-% чипы, now-line. */
interface DayColumnProps {
  day: Date;
  dayIndex: number;
  spans: PositionedSpan[];
  onEventClick?: ((event: CalendarEvent) => void) | undefined;
}

/** Часы оси: 0..23. */
const HOURS = Array.from({ length: 24 }, (_, h) => h);

/** Подпись «HH:00» для часа в оси времени. */
function hourLabel(hour: number): string {
  return `${String(hour).padStart(2, '0')}:00`;
}

/** Позиция «часы+минуты» от начала дня в % (линии и подписи). */
function topPercentOf(hour: number, extraMinutes: number): string {
  return `${((hour * 60 + extraMinutes) / MINUTES_PER_DAY) * 100}%`;
}

function DayColumn({ day, dayIndex, spans, onEventClick }: DayColumnProps): JSX.Element {
  const navigate = useNavigate();
  // S2 (W2-4): в колонке дня видимы только MAX_VISIBLE_EVENTS_PER_COLUMN чипов; остальные — «+N ещё».
  const visibleSpans = spans.slice(0, MAX_VISIBLE_EVENTS_PER_COLUMN);
  const moreSpans = spans.slice(MAX_VISIBLE_EVENTS_PER_COLUMN);
  return (
    <Box
      data-testid={`day-column-${dayIndex}`}
      sx={{ position: 'relative', flex: 1, height: HOUR_HEIGHT_PX * 24, borderLeft: dayIndex > 0 ? 1 : 0, borderColor: 'divider' }}
    >
      {HOURS.map((hour) => (
        <Box key={`hour-${hour}`} data-testid="hour-line" sx={{ position: 'absolute', top: topPercentOf(hour, 0), left: 0, right: 0, borderTop: 1, borderColor: 'divider' }} />
      ))}
      {HOURS.map((hour) => (
        <Box key={`half-${hour}`} data-testid="half-hour-line" sx={{ position: 'absolute', top: topPercentOf(hour, SLOT_MINUTES), left: 0, right: 0, borderTop: 1, borderColor: 'divider', borderStyle: 'dotted', opacity: 0.6 }} />
      ))}
      {visibleSpans.map((span) => (
        <EventChip key={span.event.id} span={span} onEventClick={onEventClick} />
      ))}
      {moreSpans.length > 0 ? (
        <Button
          data-testid="day-more"
          size="small"
          onClick={() => navigate(`/calendar/day/${format(day, 'yyyy-MM-dd', { locale: ru })}`)}
          sx={{
            position: 'absolute',
            top: `${(moreSpans[0] as PositionedSpan).topPct}%`,
            left: 0,
            minHeight: 20,
            px: 0.5,
            textTransform: 'none',
          }}
        >
          +{moreSpans.length} ещё
        </Button>
      ) : null}
      {isToday(day) ? <NowLine /> : null}
    </Box>
  );
}

/** Временная сетка: ось времени + колонки дней (см. §7). */
export function TimeGrid({ days, events, onEventClick }: TimeGridProps): JSX.Element {
  const fragments = toDayFragments(events, days);
  return (
    <Box data-testid="time-grid" sx={{ display: 'flex', width: '100%' }}>
      <Box data-testid="time-axis" sx={{ position: 'relative', width: TIME_AXIS_WIDTH_PX, flexShrink: 0, height: HOUR_HEIGHT_PX * 24 }}>
        {HOURS.map((hour) => (
          <Typography
            key={hour}
            data-testid="hour-label"
            variant="caption"
            color="text.secondary"
            sx={{ position: 'absolute', top: topPercentOf(hour, 0), right: 8, transform: 'translateY(-50%)', fontSize: '0.6875rem' }}
          >
            {hourLabel(hour)}
          </Typography>
        ))}
      </Box>
      {days.map((day, dayIndex) => {
        const spans = layoutDaySpans(fragments.filter((f) => f.dayIndex === dayIndex));
        return <DayColumn key={dayIndex} day={day} dayIndex={dayIndex} spans={spans} onEventClick={onEventClick} />;
      })}
    </Box>
  );
}
